import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast({
        title: 'Error',
        description: 'Failed to load product details',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    addToCart(product);
    toast({
      title: 'Added to cart',
      description: `${product.name} has been added to your cart`,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 animate-pulse">
            <div className="aspect-square bg-rose-100 rounded-lg"></div>
            <div>
              <div className="h-10 bg-rose-100 rounded mb-4"></div>
              <div className="h-8 bg-rose-50 rounded mb-6"></div>
              <div className="h-32 bg-rose-50 rounded mb-6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-rose-600 text-lg mb-6">Product not found</p>
          <Button onClick={() => navigate('/')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-8 text-rose-800 hover:text-rose-900 hover:bg-rose-50"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Collection
        </Button>

        <div className="grid md:grid-cols-2 gap-12">
          <Card className="overflow-hidden border-rose-100">
            <div className="aspect-square bg-white p-8">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-contain"
              />
            </div>
          </Card>

          <div>
            <h1 className="text-4xl font-serif text-rose-900 mb-4">
              {product.name}
            </h1>
            <p className="text-3xl font-serif text-rose-800 mb-8">₹{product.price}</p>

            {product.description && (
              <div className="mb-8">
                <h2 className="text-xl font-medium text-rose-900 mb-3">Description</h2>
                <p className="text-rose-700 leading-relaxed">{product.description}</p>
              </div>
            )}

            {product.stock !== undefined && (
              <div className="mb-8">
                <p className="text-sm text-rose-600">
                  {product.stock > 0 ? (
                    <span className="text-green-600">In Stock ({product.stock} available)</span>
                  ) : (
                    <span className="text-red-600">Out of Stock</span>
                  )}
                </p>
              </div>
            )}

            <Button
              onClick={handleAddToCart}
              className="w-full md:w-auto px-12 py-6 text-lg bg-rose-800 hover:bg-rose-900 text-white transition-colors"
              disabled={product.stock === 0}
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
            </Button>

            <div className="mt-12 p-6 bg-rose-50 rounded-lg">
              <h3 className="font-medium text-rose-900 mb-3">Handcrafted with Care</h3>
              <p className="text-sm text-rose-700">
                Each piece is carefully handcrafted to ensure quality and uniqueness. 
                Slight variations may occur, making your jewelry truly one-of-a-kind.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
